/**
 * Avatar — shared user-avatar primitive.
 *
 * Lifted out of `features/comments/CommentItem.tsx` (Wave 1) so the bell
 * tray (Wave 3 Batch I) and any future feature can reuse it without
 * duplicating the deterministic 6-color palette + initial logic.
 *
 * Behavior:
 *   - When `src` is set, render a circular `<img>` (object-cover, ring).
 *   - Otherwise, render a colored bubble whose background is hashed off
 *     the seed (defaults to `name`) so the same user always gets the
 *     same color across renders. Initial = first non-whitespace glyph,
 *     uppercased.
 *   - `size` is a Tailwind size class pair (defaults to 32px / `h-8 w-8`).
 *
 * The 6-color palette intentionally stays small and saturated — Linear-
 * style restraint, no rainbow.
 */

import type { ReactNode } from 'react'

const AVATAR_BG_PALETTE = [
  'bg-sky-700',
  'bg-emerald-700',
  'bg-violet-700',
  'bg-amber-700',
  'bg-rose-700',
  'bg-teal-700',
] as const

/**
 * Deterministic palette pick from a seed string. Same seed → same color
 * across renders; cheap polynomial hash, no crypto needed.
 */
export function avatarBgFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  const idx = Math.abs(hash) % AVATAR_BG_PALETTE.length
  // Defensive fallback — `idx` is always in bounds, but TS-strict + biome
  // both want a non-`!` shape here. The palette is a `const` tuple with
  // 6 entries, so `[0]` is statically present.
  return AVATAR_BG_PALETTE[idx] ?? 'bg-sky-700'
}

/**
 * The same six palette entries as SVG `fill-*` utilities.
 *
 * SVG has no `background-color`, so the reports chart kit's `<AvatarMarker>`
 * — which draws an initials `<circle>` when a portrait fails to load — cannot
 * reuse `avatarBgFor`'s class. The literals are spelled out rather than
 * derived (`bg-sky-700`.replace(...)) because Tailwind v4 scans source TEXT
 * for class names: a computed class produces no CSS at all.
 */
const AVATAR_BG_TO_FILL: Record<string, string> = {
  'bg-sky-700': 'fill-sky-700',
  'bg-emerald-700': 'fill-emerald-700',
  'bg-violet-700': 'fill-violet-700',
  'bg-amber-700': 'fill-amber-700',
  'bg-rose-700': 'fill-rose-700',
  'bg-teal-700': 'fill-teal-700',
}

/** `avatarBgFor` as an SVG fill class. Same seed → same colour as the HTML avatar. */
export function avatarFillFor(seed: string): string {
  return AVATAR_BG_TO_FILL[avatarBgFor(seed)] ?? 'fill-sky-700'
}

export interface AvatarProps {
  /** Optional avatar URL. Falls back to a colored initial bubble when null/undefined. */
  src?: string | null
  /** User's display name. Drives the initial + color hash. */
  name: string
  /**
   * Tailwind size pair (e.g. `'h-8 w-8'`). Defaults to 32px which is what
   * Wave 3 Batch I and the comment thread both use.
   */
  size?: string
  /**
   * Optional initial override. Useful when the seed for color should
   * differ from the visible initial (rare — most callers leave this).
   */
  initial?: string
  /** Override the seed used for the color hash (defaults to `name`). */
  colorSeed?: string
  /** Extra class names appended after the size + base styles. */
  className?: string
}

/**
 * The first letter or digit of a word, or `undefined` for punctuation alone.
 * "(research" gives "r", not "(": a name with a bracketed qualifier or a
 * leading symbol must still read as letters.
 */
const firstGlyph = (word: string): string | undefined => word.match(/[\p{L}\p{N}]/u)?.[0]

/**
 * Initials for a name: the first letter or digit of each word, up to `max`
 * (default one). Brackets and other punctuation are skipped, so
 * "Ada (research agent)" reads "AR", and a name with no letter or digit at
 * all falls back to "?".
 */
export function initialsFor(name: string, max = 1): string {
  const glyphs = name
    .trim()
    .split(/\s+/)
    .map(firstGlyph)
    .filter((glyph): glyph is string => glyph !== undefined)
  if (glyphs.length === 0) return '?'
  return glyphs
    .slice(0, Math.max(1, max))
    .map((glyph) => glyph.toUpperCase())
    .join('')
}

/**
 * Single-glyph avatar. Use this anywhere a user is referenced in a list
 * row, comment row, etc. For a multi-letter initial bubble, pass
 * `initial={initialsFor(name, 2)}`.
 */
export function Avatar({
  src,
  name,
  size = 'h-8 w-8',
  initial,
  colorSeed,
  className = '',
}: AvatarProps): ReactNode {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        // `ring-border`, never a hardcoded slate: in the default theme the
        // border token IS slate-200, so light themes are pixel-identical —
        // but on the dark themes a hardcoded slate-200 drew a near-WHITE
        // hairline around a 20px circle, and at that radius the contrast
        // made the anti-aliasing read as an angular outline rather than a
        // round one. The token follows the theme (slate-800 on Obsidian,
        // #003b75 on Midnight) and the edge reads as a circle again.
        className={`${size} flex-none rounded-full object-cover ring-1 ring-border ${className}`}
      />
    )
  }
  // Default to up-to-2 initials so "Omkar Totade" reads as
  // "OT", while single-word names ("Omkar") still collapse to "O".
  // Callers that explicitly want a single glyph can pass
  // `initial={initialsFor(name, 1)}`.
  const glyph = initial ?? initialsFor(name, 2)
  const bg = avatarBgFor(colorSeed ?? name)
  return (
    <span
      aria-hidden="true"
      className={`inline-flex ${size} flex-none items-center justify-center rounded-full text-xs font-semibold text-white ${bg} ${className}`}
    >
      {glyph}
    </span>
  )
}
